import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import DashboardApi from "../routes/dashboard";
import { apiLogger, authLogger } from "../Utils/logger";
import { API_BASE } from "../constants/api";
import { AUTH_TOKEN } from "../constants/localStorageKeys";
import { retrieve } from "../helpers/StorageHelper";
import { getUserTimezone } from "../helpers/TimezoneHelper";

// Weekly details query
export const useGetWeeklyDetails = (enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "weeklyDetails"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/user/weekly-details");
        const response = await DashboardApi.getWeeklyDetails();
        authLogger.apiResponse("GET", "/user/weekly-details", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/user/weekly-details",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });
};

// Monthly targets query
export const useGetMonthlyTargets = (params = {}, enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "monthlyTargets", params],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/user/dashboard/targets", params);
        const response = await DashboardApi.getMonthlyTargets(params);
        authLogger.apiResponse("GET", "/user/dashboard/targets", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/user/dashboard/targets",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
    staleTime: 3 * 60 * 1000, // 3 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// MIT tasks query
export const useGetMitTasks = (enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "mitTasks"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/tasks/mit-lists");
        const response = await DashboardApi.getMitTasks();
        authLogger.apiResponse("GET", "/tasks/mit-lists", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/tasks/mit-lists",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
    staleTime: 1 * 60 * 1000, // 1 minute
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Level details query
export const useGetLevelDetails = (enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "levelDetails"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/user/level-details");
        const response = await DashboardApi.getLevelDetails();
        authLogger.apiResponse("GET", "/user/level-details", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/user/level-details",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Journal status query
export const useGetJournalStatus = (reflectionType = "am", enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "journalStatus", reflectionType],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/reflections/type/status", {
          reflection_type: reflectionType,
        });
        const response = await DashboardApi.getJournalStatus(reflectionType);
        authLogger.apiResponse(
          "GET",
          "/reflections/type/status",
          response,
          200,
        );

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/reflections/type/status",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
  });
};

// Habits query
export const useGetHabits = (fromDate, toDate, enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "habits", fromDate, toDate],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/user/habits", {
          from_date: fromDate,
          to_date: toDate,
        });
        const response = await DashboardApi.getHabits(fromDate, toDate);
        authLogger.apiResponse("GET", "/user/habits", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/user/habits",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled: enabled && !!fromDate && !!toDate,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Emotion names query
export const useGetEmotionNames = (enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "emotionNames"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/journals/emotion-names");
        const response = await DashboardApi.getEmotionNames();
        authLogger.apiResponse("GET", "/journals/emotion-names", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/journals/emotion-names",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
    staleTime: 10 * 60 * 1000, // 10 minutes - emotion names don't change often
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Recent courses (dashboard)
export const useGetRecentCourses = () => {
  const hasToken = !!retrieve(AUTH_TOKEN);

  return useQuery({
    queryKey: ["dashboard", "recentCourses"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/v6/recent/course");
        const response = await DashboardApi.getRecentCourses();
        authLogger.apiResponse("GET", "/v6/recent/course", response, 200);

        if (response?.success && response?.data) {
          return {
            recent_courses: Array.isArray(response.data.recent_courses)
              ? response.data.recent_courses
              : [],
            all_courses_completed: Boolean(response.data.all_courses_completed),
          };
        }
        return { recent_courses: [], all_courses_completed: false };
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/v6/recent/course",
          error,
          error.response?.status,
        );
        return { recent_courses: [], all_courses_completed: false };
      }
    },
    enabled: hasToken,
    retry: 1,
    staleTime: 0,
    refetchOnMount: "always",
    cacheTime: 5 * 60 * 1000,
  });
};

/**
 * Same API sequence as CourseModuleView before navigating to module chapters
 * (recent course "Jump Back In" from dashboard).
 */
export const prepareDashboardRecentCourseModule = async (
  moduleId,
  chapterId,
) => {
  const token = retrieve(AUTH_TOKEN);
  if (!token) {
    throw new Error("Missing auth token");
  }

  try {
    authLogger.apiRequest("GET", `/user/modules/${moduleId}/chapters`);
    const chaptersResponse = await DashboardApi.getModuleChapters(moduleId);
    authLogger.apiResponse(
      "GET",
      `/user/modules/${moduleId}/chapters`,
      chaptersResponse,
      200,
    );

    if (chapterId != null) {
      const body = {
        module_id: moduleId,
        chapter_id: chapterId,
        status: "in_progress",
      };
      authLogger.apiRequest("POST", "/user/module-chapter/update-status", body);
      const updateResponse = await DashboardApi.updateModuleChapterStatus(body);
      authLogger.apiResponse(
        "POST",
        "/user/module-chapter/update-status",
        updateResponse,
        200,
      );
    }
  } catch (error) {
    authLogger.apiError(
      "GET/POST",
      "dashboard-recent-course-module-resume",
      error,
      error.response?.status,
    );
    throw error;
  }
};

// User streak queries
export const useGetUserStreaks = (enabled = true) => {
  return useQuery({
    queryKey: ["dashboard", "userStreaks"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/v3/streak");
        // Keep streak call isolated from shared axios interceptors so it is not blocked by other API flows.
        const token = retrieve(AUTH_TOKEN);
        const response = await axios.get(`${API_BASE}/v3/streak`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "X-Timezone": getUserTimezone(),
          },
        });
        authLogger.apiResponse(
          "GET",
          "/v3/streak",
          response.data,
          response.status,
        );

        // Normalize backend shapes: {data: {...}} or plain object
        return response?.data?.data || response?.data || {};
      } catch (error) {
        authLogger.apiError("GET", "/v3/streak", error, error.response?.status);
        throw error;
      }
    },
    enabled,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const completeJournalHabitForDate = async (habitDate) => {
  try {
    authLogger.apiRequest("GET", "/user/habits", {
      from_date: habitDate,
      to_date: habitDate,
    });
    const habitsResponse = await DashboardApi.getHabits(habitDate, habitDate);
    authLogger.apiResponse("GET", "/user/habits", habitsResponse, 200);

    // Backend shapes vary: sometimes { data: { data: ... } }, sometimes { data: ... }
    const habitsData = habitsResponse?.data?.data ?? habitsResponse?.data ?? {};

    // Normalize to a flat list of "habit objects" so we can safely search
    const habitCandidates = [];
    const pushCandidate = (habit) => {
      if (habit && typeof habit === "object") habitCandidates.push(habit);
    };

    if (Array.isArray(habitsData)) {
      habitsData.forEach(pushCandidate);
    } else if (habitsData && typeof habitsData === "object") {
      for (const categoryHabits of Object.values(habitsData)) {
        if (Array.isArray(categoryHabits)) {
          categoryHabits.forEach(pushCandidate);
        } else if (categoryHabits && typeof categoryHabits === "object") {
          Object.values(categoryHabits).forEach(pushCandidate);
        }
      }
    }

    const journalHabitExact = habitCandidates.find(
      (habit) =>
        typeof habit.type_name === "string" &&
        habit.type_name.toLowerCase() === "journal",
    );

    // Fallback: sometimes backend uses values like "Daily Journal"
    const journalHabitPartial = habitCandidates.find(
      (habit) =>
        typeof habit.type_name === "string" &&
        habit.type_name.toLowerCase().includes("journal"),
    );

    const journalHabit = journalHabitExact || journalHabitPartial || null;

    if (!journalHabit?.id) {
      return { skipped: true, reason: "habit_not_found" };
    }

    const alreadyCompleted =
      journalHabit?.tracked_data?.[0]?.status === 1 ||
      journalHabit?.tracked_data?.find((d) => d.habit_date === habitDate)
        ?.status === 1;

    if (alreadyCompleted) {
      return { skipped: true, reason: "already_completed" };
    }

    apiLogger.apiRequest("POST", "/user/habit/update-status", {
      habits_id: journalHabit.id,
      habit_date: habitDate,
      status: 1,
    });
    const updateResponse = await DashboardApi.updateHabitStatus(
      journalHabit.id,
      habitDate,
      1,
    );
    apiLogger.apiResponse(
      "POST",
      "/user/habit/update-status",
      updateResponse,
      200,
    );
    return { skipped: false };
  } catch (error) {
    apiLogger.apiError(
      "POST",
      "/user/habit/update-status",
      error,
      error.response?.status,
    );
    throw error;
  }
};
